import { cellarMetrics, METRIC_TYPES, type BottleCount } from '../domain/cellar-metrics';
import type { Cellar } from '../domain/cellar';
import { Link } from 'react-router-dom';
import { t } from '../i18n';
import { wineTypeLabel } from '../i18n/errors';
import './CellarMetricsBar.css';

/** "3 / 12 bouteilles": bottles left, out of bottles left plus drunk. */
function Count({ count }: { count: BottleCount }) {
  return (
    <>
      <span className="cellar-metrics__value">
        {count.available} / {count.total}
      </span>{' '}
      <span className="cellar-metrics__unit">
        {t(count.total === 1 ? 'inventory.bottleUnit.one' : 'inventory.bottleUnit.other')}
      </span>
    </>
  );
}

/**
 * Key figures at the top of a cellar's home page (owner feedback, 2026-09-28; changed 2026-10-03
 * and 2026-10-06): "Stock" (left / left plus drunk) and "À boire" (bottles in stock past their
 * date) on the first row, then rosé, white, red and late harvest wines (owner, 2026-10-15), each
 * opening "Stock" with its filter.
 */
export function CellarMetricsBar({ cellar, base }: { cellar: Cellar; base: string }) {
  const metrics = cellarMetrics(cellar);
  const unit = (count: number) =>
    t(count === 1 ? 'inventory.bottleUnit.one' : 'inventory.bottleUnit.other');

  return (
    <nav className="cellar-metrics" aria-label={t('inventory.metrics')}>
      <Link to={`${base}/stock`} className="cellar-metrics__item">
        <span className="cellar-metrics__title">{t('inventory.metricBottles')}</span>
        <span className="cellar-metrics__figure">
          <Count count={metrics.bottles} />
        </span>
      </Link>
      <Link
        to={`${base}/stock?alert=past`}
        className={
          metrics.expiredBottles > 0
            ? 'cellar-metrics__item cellar-metrics__item--alert'
            : 'cellar-metrics__item'
        }
      >
        <span className="cellar-metrics__title">{t('inventory.metricExpired')}</span>
        <span className="cellar-metrics__figure">
          <span className="cellar-metrics__value">{metrics.expiredBottles}</span>{' '}
          <span className="cellar-metrics__unit">{unit(metrics.expiredBottles)}</span>
        </span>
      </Link>
      {METRIC_TYPES.map((type) => (
        <Link
          key={type}
          to={`${base}/stock?type=${type}`}
          className={`cellar-metrics__item cellar-metrics__item--${type}`}
        >
          <span className="cellar-metrics__title">{wineTypeLabel(type)}</span>
          <span className="cellar-metrics__figure">
            <Count count={metrics.byType[type]} />
          </span>
        </Link>
      ))}
    </nav>
  );
}
